import {
  AuthContext,
  type AuthContextValue,
  type SignInPayload,
  CYNTHIA_DIALOG_ROUTE,
  DEFAULT_AUTH_REDIRECT_ROUTE,
  isSurferUser,
  isSurferWelcomePending,
} from '@entities/auth';
import { useClearStore } from '@entities/auth/hooks';
import { useSetState } from '@mantine/hooks';
import { ECookies } from '@shared/config';

import React, { type PropsWithChildren, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useNavigate, useLocation } from 'react-router';
import { useShowMe } from '@entities/auth/api';
import { checkPublicRoutes } from './helpers';
import { AUTH_ROUTES } from './constants';

export const SessionProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const clearStore = useClearStore();
  const navigate = useNavigate();
  const {
    data,
    isPending: isPendingUser,
    isFetching: isFetchingUser,
    isError,
    refetch,
  } = useShowMe();

  // Проверяем наличие необходимых токенов для авторизации
  const token = Cookies.get(ECookies.TOKEN);
  const tokenType = Cookies.get(ECookies.TOKEN_TYPE);

  const hasValidTokens = !!token && !!tokenType;

  const [authState, setAuthState] = useSetState<AuthContextValue>({
    isAuthenticated: false,
    user: null,
    signIn: () => undefined,
    signOut: () => undefined,
    isLoading: true,
  });

  // Состояние для отслеживания целевого роута при логауте
  const [targetRoute, setTargetRoute] = useState<string | null>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    // Если есть целевой роут и он публичный - не редиректим
    if (targetRoute && checkPublicRoutes(targetRoute)) {
      return;
    }

    // Если нет токенов и мы не на публичном роуте
    if (!hasValidTokens && !checkPublicRoutes(pathname)) {
      clearStore();
      navigate('/auth');
    }
  }, [hasValidTokens, pathname, navigate, targetRoute]);

  useEffect(() => {
    if (targetRoute && pathname === targetRoute) {
      setTargetRoute(null);
    }
  }, [pathname, targetRoute]);

  useEffect(() => {
    // Если нет токенов, устанавливаем состояние как неавторизованное
    if (!hasValidTokens) {
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
      });
      return;
    }

    // Если есть ошибка при получении данных пользователя
    if (isError) {
      clearStore();
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
      });
      // Не делаем редирект если уже на публичном роуте
      if (!checkPublicRoutes(pathname)) {
        navigate('/auth');
      }
      return;
    }

    // Если данные пользователя загружаются
    if (isPendingUser || isFetchingUser) {
      setAuthState(prev => ({
        ...prev,
        isLoading: true,
      }));
      return;
    }

    // Если данные пользователя получены успешно
    if (data) {
      const token = Cookies.get(ECookies.TOKEN);
      const tokenExpiresIn = Cookies.get(ECookies.TOKEN_EXPIRES_IN);

      setAuthState({
        isAuthenticated: true,
        user: {
          tokenExpiresIn: Number(tokenExpiresIn),
          token: String(token),
          ...data,
        },
        isLoading: false,
      });

      // Если пользователь на странице авторизации, перенаправляем в ЛК
      if (AUTH_ROUTES.includes(pathname)) {
        if (isSurferUser(data)) {
          if (pathname === '/surfer-register' && isSurferWelcomePending()) {
            return;
          }

          navigate(CYNTHIA_DIALOG_ROUTE);
          return;
        }

        navigate(DEFAULT_AUTH_REDIRECT_ROUTE);
      }
    }
  }, [hasValidTokens, isFetchingUser, isPendingUser, isError, data, pathname]);

  const value: AuthContextValue = React.useMemo(
    () => ({
      ...authState,
      signIn: (payload: SignInPayload) => {
        Cookies.set(ECookies.TOKEN, payload.token);
        Cookies.set(ECookies.TOKEN_TYPE, payload.tokenType);
        Cookies.set(
          ECookies.TOKEN_EXPIRES_IN,
          payload.tokenExpiresIn.toString()
        );
        Cookies.set(ECookies.REFRESH_TOKEN, payload.refreshToken);
        setAuthState({
          isAuthenticated: true,
          user: { ...payload },
        });
        refetch();
      },
      signOut: (redirectTo?: string) => {
        const targetPath = redirectTo || '/auth';

        setTargetRoute(targetPath);
        navigate(targetPath);

        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
        });
        clearStore();
      },
    }),
    [authState]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
